import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';

export function useLoadConfig(permitdata) {
  const queryClient = useQueryClient();
  const shopId = permitdata?.shop_id;
  const enabled = !!shopId && shopId !== '-';

  const [params, setParams] = useState({
    page: 1,
    tab: 'onsell',
    search: '',
    sellerOnly: false,
    expireFilter: 'all',
  });

  const queryKey = [
    'load-config',
    shopId,
    params.page,
    params.tab,
    params.search,
    params.sellerOnly,
    params.expireFilter,
  ];

  const { data, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const formData = new FormData();
      formData.append('shop_id', shopId);
      formData.append('page', String(params.page));
      formData.append('tab', params.tab);
      formData.append('search', params.search);
      if (params.sellerOnly) formData.append('seller_only', '1');
      if (params.expireFilter && params.expireFilter !== 'all') {
        formData.append('expire_filter', params.expireFilter);
      }

      const response = await client.post('/api/product/common/loadconfig', formData, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.data.status !== 'OK') {
        throw new Error(response.data.message || 'loadconfig failed');
      }
      return response.data;
    },
    enabled,
    placeholderData: (previousData, previousQuery) => {
      if (!previousData || !previousQuery) return undefined;
      const prev = previousQuery.queryKey;
      const sameTabScope =
        prev[1] === shopId &&
        prev[3] === params.tab &&
        prev[4] === params.search &&
        prev[5] === params.sellerOnly;
      return sameTabScope ? previousData : undefined;
    },
  });

  // Keep Navbar / MyShop alert icons in sync (shop-wide; skip when list is search-filtered).
  useEffect(() => {
    if (!shopId || !data || params.search) return;
    const near = data.near_expire_count ?? 0;
    const expired = data.tab_counts?.expired ?? 0;
    queryClient.setQueryData(['expire-alert-counts', shopId], {
      near,
      expired,
      hasAlert: near + expired > 0,
    });
  }, [data, shopId, params.search, queryClient]);

  const load = useCallback(
    (page = 1, tab = 'onsell', search = '', sellerOnly = false, expireFilter = 'all') => {
      setParams({ page, tab, search, sellerOnly, expireFilter: expireFilter || 'all' });
    },
    [],
  );

  const reload = useCallback(() => {
    refetch();
  }, [refetch]);

  const reset = useCallback(() => {
    setParams({ page: 1, tab: 'onsell', search: '', sellerOnly: false, expireFilter: 'all' });
    queryClient.removeQueries({ queryKey: ['load-config'] });
  }, [queryClient]);

  const setCartCount = useCallback(
    (updater) => {
      queryClient.setQueriesData({ queryKey: ['load-config', shopId] }, (old) => {
        if (!old) return old;
        const next = typeof updater === 'function' ? updater(old.cart_count ?? 0) : updater;
        return { ...old, cart_count: next };
      });
    },
    [queryClient, shopId],
  );

  const setOrderCount = useCallback(
    (updater) => {
      queryClient.setQueriesData({ queryKey: ['load-config', shopId] }, (old) => {
        if (!old) return old;
        const next = typeof updater === 'function' ? updater(old.order_count ?? 0) : updater;
        return { ...old, order_count: next };
      });
    },
    [queryClient, shopId],
  );

  return {
    categories: data?.products_category ?? [],
    buyerCategory: data?.buyer_category ?? [],
    sellLicenses: data?.sell_licenses ?? [],
    cartCount: data?.cart_count ?? 0,
    setCartCount,
    orderCount: data?.order_count ?? 0,
    setOrderCount,
    shopItems: data?.products_shopitem ?? [],
    shopItemTotal: data?.shopitem_total ?? 0,
    nearExpireCount: data?.near_expire_count ?? 0,
    nearExpireCountAll: data?.near_expire_count_all ?? 0,
    nearExpireCountPro: data?.near_expire_count_pro ?? 0,
    tabCounts: data?.tab_counts ?? { all: 0, pro: 0, blank: 0, expired: 0, unlicensed: 0 },
    loading: isFetching,
    load,
    reload,
    reset,
  };
}
